import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Instagram, Facebook, Twitter } from 'lucide-react';
import { useLocation, useParams } from 'react-router-dom';

export default function Playerprofile() {
    const [playerData, setPlayerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const location = useLocation()
    const id = location.state?.athlete

    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${process.env.REACT_APP_MINF_LATEST_BAC_BASE}/api/player/${id}/profile`);
                setPlayerData(response.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (!id) {
        return (
            <div className="min-h-screen flex items-center justify-center text-gray-500 text-xl font-semibold">
                No data is there
            </div>
        );
    }

    const getMedalCounts = (achievements) => {
        const counts = { Gold: 0, Silver: 0, Bronze: 0 };
        if (!achievements) return counts;
        achievements.forEach(a => {
            if (a.medal === 'Gold') counts.Gold++;
            if (a.medal === 'Silver') counts.Silver++;
            if (a.medal === 'Bronze') counts.Bronze++;
        });
        return counts;
    };

    const getMedalIcon = (medal) => {
        const icons = {
            Gold: "🥇",
            Silver: "🥈",
            Bronze: "🥉"
        };
        return icons[medal] || "🏅";
    };

    if (loading) return (
        <section className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="w-full max-w-6xl p-6 animate-pulse">
                {/* Banner Skeleton */}
                <div className="mx-5 mb-6 rounded-lg relative overflow-hidden shadow-xl h-[200px] bg-gray-200" />

                {/* Header Card Skeleton */}
                <div className="bg-gradient-to-br from-white to-gray-50 relative rounded-3xl overflow-hidden p-8 mb-10 border border-gray-200 shadow-2xl">
                    <div className="flex sm:flex-row flex-col items-center sm:space-x-8 space-y-6 sm:space-y-0">
                        {/* Avatar Skeleton */}
                        <div className="relative w-36 h-36 rounded-full overflow-hidden shadow-lg bg-gray-200" />
                        {/* Profile Info Skeleton */}
                        <div className="flex-1 text-center sm:text-left">
                            <div className="h-8 w-48 bg-gray-200 rounded mb-2 mx-auto sm:mx-0" />
                            <div className="h-5 w-32 bg-gray-200 rounded mb-3 mx-auto sm:mx-0" />
                            <div className="flex flex-wrap justify-center sm:justify-start gap-4">
                                <div className="bg-gray-200 h-8 w-20 rounded-xl" />
                                <div className="bg-gray-200 h-8 w-20 rounded-xl" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Grid Skeleton */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left: Medal & Achievements Skeleton */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Medal Display Skeleton */}
                        <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-xl">
                            <div className="h-6 w-40 bg-gray-200 rounded mb-6" />
                            <div className="grid grid-cols-3 gap-6">
                                {[...Array(3)].map((_, i) => (
                                    <div className="text-center" key={i}>
                                        <div className="h-14 w-14 mx-auto bg-gray-200 rounded-full mb-3" />
                                        <div className="h-8 w-8 mx-auto bg-gray-200 rounded mb-2" />
                                        <div className="h-4 w-12 mx-auto bg-gray-200 rounded" />
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Achievements Skeleton */}
                        <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-sm">
                            <div className="h-6 w-32 bg-gray-200 rounded mb-6" />
                            <div className="space-y-3">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-gray-200" />
                                            <div>
                                                <div className="h-4 w-24 bg-gray-200 rounded mb-1" />
                                                <div className="h-3 w-12 bg-gray-200 rounded" />
                                            </div>
                                        </div>
                                        <div className="h-4 w-10 bg-gray-200 rounded" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Right: Sidebar Skeleton */}
                    <div className="space-y-6">
                        {/* Player Info Skeleton */}
                        <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-xl">
                            <div className="h-5 w-24 bg-gray-200 rounded mb-4" />
                            <div className="space-y-3">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="flex justify-between">
                                        <div className="h-4 w-16 bg-gray-200 rounded" />
                                        <div className="h-4 w-16 bg-gray-200 rounded" />
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Medal Summary Skeleton */}
                        <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-xl">
                            <div className="h-5 w-24 bg-gray-200 rounded mb-4" />
                            <div className="space-y-3">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="flex justify-between">
                                        <div className="h-4 w-12 bg-gray-200 rounded" />
                                        <div className="h-4 w-8 bg-gray-200 rounded" />
                                    </div>
                                ))}
                                <div className="border-t border-gray-200 pt-3 mt-3">
                                    <div className="flex justify-between">
                                        <div className="h-4 w-12 bg-gray-200 rounded" />
                                        <div className="h-4 w-8 bg-gray-200 rounded" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Social Media Skeleton */}
                        <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-xl">
                            <div className="h-5 w-24 bg-gray-200 rounded mb-4" />
                            <div className="flex space-x-4">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="w-6 h-6 bg-gray-200 rounded-full" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
    if (error) return <div className="min-h-screen flex items-center justify-center text-red-500">{error}</div>;
    if (!playerData) return null;

    const medalCounts = getMedalCounts(playerData.achievements);
    const totalMedals = medalCounts.Gold + medalCounts.Silver + medalCounts.Bronze;

    return (
        <section className="min-h-screen">
            {/* Banner */}
            <div
                className="mx-5 mb-6 rounded-lg relative overflow-hidden shadow-xl"
                style={{
                    backgroundImage: "url('https://i.pinimg.com/736x/96/5c/8f/965c8f6a1faedc80d75e670288001547.jpg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    height: "200px"
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
                <div className="relative flex h-full justify-center items-center">
                    <div className="text-center">
                        <h1 className="px-8 text-6xl mt-12 md:text-7xl text-center text-white tracking-widest interf pb-2 font-bold animate-fade-in">
                            {playerData.name.split(" ")[0]}
                        </h1>
                    </div>
                </div>
            </div>

            {/* Main Container */}
            <div className="min-h-screen text-gray-900">
                <div className="max-w-6xl mx-auto p-6">
                    {/* Header Card */}
                    <div className="bg-gradient-to-br from-white to-gray-50 relative rounded-3xl overflow-hidden p-8 mb-10 border border-gray-200 shadow-2xl">
                        <div className="flex sm:flex-row flex-col items-center sm:space-x-8 space-y-6 sm:space-y-0">
                            {/* Avatar */}
                            <div className="relative w-36 h-36 rounded-full overflow-hidden shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-200 via-white to-indigo-100 p-[3px] rounded-full" />
                                <img
                                    src={playerData.photo}
                                    alt={playerData.name}
                                    onError={(e) => (e.target.src = "https://via.placeholder.com/150")}
                                    className="relative w-full h-full object-cover rounded-full border-4 border-white z-10"
                                />
                            </div>

                            {/* Profile Info */}
                            <div className="flex-1 text-center sm:text-left">
                                <h1 className="text-4xl font-extrabold text-gray-900 mb-1">{playerData.name}</h1>
                                <p className="text-lg text-gray-600 font-medium mb-3">{playerData.state}</p>
                                <div className="flex flex-wrap justify-center sm:justify-start gap-4 text-gray-700">
                                    <div className="bg-gray-100 px-4 py-2 rounded-xl shadow-sm">Age: {playerData.age}</div>
                                    <div className="bg-gray-100 px-4 py-2 rounded-xl shadow-sm">Rank: {playerData.rank}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Left: Medal & Achievements */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Medal Display */}
                            <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-xl">
                                <h2 className="text-2xl font-bold mb-6 text-gray-900">Medal Collection</h2>
                                <div className="grid grid-cols-3 gap-6">
                                    <div className="text-center">
                                        <div className="text-5xl mb-3">🥇</div>
                                        <div className="text-3xl font-bold mb-2 text-yellow-600">{medalCounts.Gold}</div>
                                        <div className="text-gray-500">Gold</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-5xl mb-3">🥈</div>
                                        <div className="text-3xl font-bold mb-2 text-gray-400">{medalCounts.Silver}</div>
                                        <div className="text-gray-500">Silver</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-5xl mb-3">🥉</div>
                                        <div className="text-3xl font-bold mb-2 text-orange-500">{medalCounts.Bronze}</div>
                                        <div className="text-gray-500">Bronze</div>
                                    </div>
                                </div>
                            </div>

                            {/* Achievements */}
                            <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-sm">
                                <h2 className="text-2xl font-semibold mb-6 text-gray-800">Achievements</h2>
                                <div className="space-y-3">
                                    {playerData.achievements && playerData.achievements.map((event, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl bg-white border border-gray-300">
                                                    {getMedalIcon(event.medal)}
                                                </div>
                                                <div>
                                                    <div className="text-gray-900 font-medium">{event.event}</div>
                                                    <div className="text-sm text-gray-400">{event.year}</div>
                                                    {event.link && (
                                                        <a
                                                            href={event.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-500 underline text-xs"
                                                        >
                                                            Certificate
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-700 font-semibold">{event.medal}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right: Sidebar */}
                        <div className="space-y-6">
                            {/* Player Info */}
                            <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-xl">
                                <h3 className="text-xl font-bold mb-4 text-gray-900">Player Info</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">State</span>
                                        <span className="font-medium text-gray-900">{playerData.state}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Age</span>
                                        <span className="font-medium text-gray-900">{playerData.age}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Gender</span>
                                        <span className="font-medium text-gray-900">{playerData.gender}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Height</span>
                                        <span className="font-medium text-gray-900">{playerData.height} cm</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Weight Category</span>
                                        <span className="font-medium text-gray-900">{playerData.weightCategory}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Medal Summary */}
                            <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-xl">
                                <h3 className="text-xl font-bold mb-4 text-gray-900">Medal Summary</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Gold</span>
                                        <span className="font-medium text-yellow-600">{medalCounts.Gold}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Silver</span>
                                        <span className="font-medium text-gray-400">{medalCounts.Silver}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Bronze</span>
                                        <span className="font-medium text-orange-500">{medalCounts.Bronze}</span>
                                    </div>
                                    <div className="border-t border-gray-200 pt-3 mt-3">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Total</span>
                                            <span className="font-bold text-gray-900">{totalMedals}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Social Media */}
                            <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-xl">
                                <h3 className="text-xl font-bold mb-4 text-gray-900">Social Media</h3>
                                <div className="flex space-x-4">
                                    {playerData.socialLinks?.instagram && (
                                        <a href={playerData.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-pink-500">
                                            <Instagram className="w-6 h-6" />
                                        </a>
                                    )}
                                    {playerData.socialLinks?.facebook && (
                                        <a href={playerData.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                                            <Facebook className="w-6 h-6" />
                                        </a>
                                    )}
                                    {playerData.socialLinks?.twitter && (
                                        <a href={playerData.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-sky-400">
                                            <Twitter className="w-6 h-6" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
